import React, { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { CaretLeft } from "@phosphor-icons/react";
import {
  clearInboxList,
  fetchInboxItems,
  fetchInboxUnreadCount,
  selectInboxFilters,
  selectSelectedInboxItem,
  setSelectedInboxDocument,
} from "../../slice/InboxSlice";
import InboxList from "./components/InboxList";
import MobileBriefingSection from "./components/MobileBriefingSection";
import MobileDocumentViewer from "../../components/MobileDocumentViewer/MobileDocumentViewer";
import InboxActionFooter from "../../components/AIPane/InboxActionFooter";
import InboxDuplicateBanner from "./components/InboxDuplicateBanner";
import useWaitingOnWorkItems from "../../hooks/useWaitingOnWorkItems";
import useInboxProcessed from "../../hooks/useInboxProcessed";
import {
  DetailBackButton,
  DetailHeader,
  DetailSubtitle,
  DetailTitle,
  DetailTitleBlock,
  DetailViewerArea,
  MobileInboxWrapper,
} from "./MobileInboxStyled";

/**
 * Mobile Attorney Inbox (Mobile DMS v1, Phase 2).
 *
 * Stacked flow instead of the desktop 3-pane layout:
 *
 *   /inbox              → full-width list (reuses `InboxList` +
 *                          `InboxSlice` verbatim)
 *   /inbox/:documentId  → full-height detail screen:
 *                          back header / collapsible AI briefing /
 *                          lightweight viewer / action footer
 *
 * Everything below the render fork is shared with desktop: the same
 * slice thunks, the same `InboxActionFooter` write surface (mark
 * reviewed auto-advances to `/inbox/<nextId>`, which on mobile is the
 * next detail screen — exactly the triage loop we want), the same
 * URL contract. Only the document viewer differs — `MobileDocumentViewer`
 * (react-pdf) instead of Apryse, which is too heavy for phones.
 */

// Width override for the desktop list pane (a fixed ~360px aside).
// Scoped wrapper instead of a prop change so this file doesn't touch
// `InboxStyled.js` / `InboxList.js`, which other open PRs own.
const FluidListArea = styled.div`
  height: 100%;
  min-height: 0;
  display: flex;

  & > aside {
    width: 100%;
    max-width: none;
  }
`;

const MobileInbox = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { documentId: documentIdParam } = useParams();

  const filters = useSelector(selectInboxFilters);
  const selectedItem = useSelector(selectSelectedInboxItem);

  // Work-items context for the open document's matter — drives the
  // footer's one-door gate, same as the desktop AIPane. Null (no
  // document open, or lookup failed) means no hiding.
  const { waitingOn, reload: reloadWaitingOn } = useWaitingOnWorkItems(
    documentIdParam ?? null
  );

  // Attach = processed, same one motion as the desktop AIPane: mark
  // reviewed and advance to the next unread document. A deep-linked
  // document with no row on the current page is treated as read — only
  // mark-and-advance when we KNOW it's unread (Bugbot, #641).
  const markProcessed = useInboxProcessed(
    documentIdParam ?? null,
    selectedItem ? Boolean(selectedItem.is_read) : true
  );
  const handleWorkItemAttached = useCallback(() => {
    reloadWaitingOn();
    markProcessed();
  }, [reloadWaitingOn, markProcessed]);

  // Mirror the URL's :documentId into the slice — same contract as the
  // desktop page (URL is the source of truth).
  useEffect(() => {
    dispatch(setSelectedInboxDocument(documentIdParam ?? null));
  }, [dispatch, documentIdParam]);

  // Initial load + refire on filter changes; page changes are owned by
  // InboxList's own pagination dispatches.
  useEffect(() => {
    dispatch(
      fetchInboxItems({
        matterId: filters.matterId,
        includeSnoozed: filters.includeSnoozed,
        recentlyReviewed: filters.recentlyReviewed,
        category: filters.category,
        page: 1,
        pageSize: 20,
      })
    );
    dispatch(fetchInboxUnreadCount());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filters.matterId, filters.includeSnoozed, filters.recentlyReviewed]);

  // Drop the cached list when leaving /inbox entirely (the component
  // stays mounted across list ↔ detail transitions, so this only
  // fires on true navigation away — matching desktop).
  useEffect(
    () => () => {
      dispatch(clearInboxList());
    },
    [dispatch]
  );

  const handleSelectDocument = useCallback(
    (id) => {
      if (id == null) return;
      navigate(`/inbox/${id}`);
    },
    [navigate]
  );

  const handleBack = useCallback(() => {
    navigate("/inbox");
  }, [navigate]);

  // Deep-link fallback: the row may live on another page of the list.
  // The detail surfaces fetch their own per-doc data, so a sparse item
  // is enough for the footer + header.
  const fallbackItem = useMemo(() => {
    if (!documentIdParam || selectedItem) return null;
    return { id: documentIdParam, name: "", matter: null };
  }, [documentIdParam, selectedItem]);

  const item = selectedItem || fallbackItem;

  if (!documentIdParam) {
    return (
      <MobileInboxWrapper>
        <FluidListArea>
          <InboxList
            selectedDocumentId={null}
            onSelectDocument={handleSelectDocument}
          />
        </FluidListArea>
      </MobileInboxWrapper>
    );
  }

  const matter = item?.matter || null;

  return (
    <MobileInboxWrapper>
      <DetailHeader>
        <DetailBackButton
          type="button"
          onClick={handleBack}
          aria-label="Back to inbox"
        >
          <CaretLeft size={20} weight="bold" />
        </DetailBackButton>
        <DetailTitleBlock>
          <DetailTitle>{item?.name || "Document"}</DetailTitle>
          {matter?.name && (
            <DetailSubtitle>
              {matter.name}
              {matter.matter_number ? ` · ${matter.matter_number}` : ""}
            </DetailSubtitle>
          )}
        </DetailTitleBlock>
      </DetailHeader>

      {/* Same guard as desktop: only the real row payload carries a
          duplicate flag, never the deep-link fallback. */}
      {selectedItem && <InboxDuplicateBanner item={selectedItem} />}

      <MobileBriefingSection documentId={documentIdParam} />

      <DetailViewerArea>
        <MobileDocumentViewer
          documentId={documentIdParam}
          documentName={item?.name || ""}
        />
      </DetailViewerArea>

      {/* Same one-door gate as the desktop AIPane: a migrated matter
          hides the Litify-bound Create Task on phones too (Bugbot,
          PR #636). The hook resolves to null on failure — no hiding. */}
      <InboxActionFooter
        documentId={documentIdParam}
        item={item}
        hideCreateTask={Boolean(waitingOn?.matter_found)}
        onWorkItemAttached={handleWorkItemAttached}
      />
    </MobileInboxWrapper>
  );
};

export default MobileInbox;
